import { supabaseClient } from '../supabase'
import baseDataProvider from '../supabase/baseDataProvider'
import { MAX_STATUS_HISTORY } from './constants'

const parcoursupDataProvider = {
	...baseDataProvider,

	// Upsert for evolution daily data entry
	upsertEvolution: async (params) => {
		const { data: rows, center_id, year_id, created_by } = params

		const upsertRows = rows.map(row => ({
			faculte: row.faculte,
			mineure: row.mineure,
			date: row.date,
			dernier_appele: row.dernier_appele,
			center_id,
			year_id,
			created_by,
		}))

		const { data, error } = await supabaseClient
			.from('parcoursup_evolution')
			.upsert(upsertRows, { onConflict: 'faculte,mineure,date,center_id' })
			.select()

		if ( error ) throw new Error(error.message)
		return { data }
	},

	// Bulk update for auto-assignment
	bulkUpdateLeadsParcoursup: async (updates) => {
		const errors = []

		for ( const update of updates ) {
			const { id, ...fields } = update
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.update(fields)
				.eq('id', id)

			if ( error ) errors.push(error)
		}

		if ( errors.length > 0 ) {
			throw new Error(`Bulk update errors: ${errors.map(e => e.message).join(', ')}`)
		}

		return { data: updates.map(u => u.id) }
	},

	// Bulk upsert for historique N-1 import
	bulkUpsertHistorique: async ({ rows, center_id, year_id }) => {
		const upsertRows = rows.map(row => ({
			faculte: row.faculte,
			mineure: row.mineure,
			dernier_pris_n1: row.dernier_pris_n1,
			places: row.places,
			center_id,
			year_id,
		}))

		const { data, error } = await supabaseClient
			.from('parcoursup_historique')
			.upsert(upsertRows, { onConflict: 'faculte,mineure,center_id,year_id' })
			.select()

		if ( error ) throw new Error(error.message)
		return { data }
	},

	// Update lead status + push to status_history
	updateLeadStatus: async ({ id, leadId, status, previousStatus, statusHistory = [] }) => {
		const now = new Date().toISOString()
		const historyEntry = { from: previousStatus || null, to: status, date: now }
		const trimmed = statusHistory.slice(-(MAX_STATUS_HISTORY - 1))
		const data = {
			status,
			status_history: [...trimmed, historyEntry],
			last_attempt: now.split('T')[0],
		}

		if ( id ) {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.update(data)
				.eq('id', id)
			if ( error ) throw new Error(error.message)
		} else {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.insert({ lead_id: leadId, ...data })
				.select()
			if ( error ) throw new Error(error.message)
		}

		return { data: { id: id || leadId } }
	},

	// Toggle LAS flag
	toggleLas: async ({ id, leadId, value }) => {
		const data = { las: value }

		if ( id ) {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.update(data)
				.eq('id', id)
			if ( error ) throw new Error(error.message)
		} else {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.insert({ lead_id: leadId, ...data })
				.select()
			if ( error ) throw new Error(error.message)
		}

		return { data: { id: id || leadId } }
	},

	// Toggle hors_sante with status backup/restore
	toggleHorsSante: async ({ id, leadId, value, currentStatus, currentRappelDate }) => {
		let data

		if ( value ) {
			// Marking as hors sante: backup current status and rappel_date
			data = {
				hors_sante: true,
				status_before_hs: currentStatus || null,
				rappel_date_before_hs: currentRappelDate || null,
			}
		} else {
			// Unmarking hors sante: restore from backups
			data = {
				hors_sante: false,
				status_before_hs: null,
				rappel_date_before_hs: null,
			}
		}

		if ( id ) {
			if ( !value ) {
				// When restoring, fetch the backups first
				const { data: record, error: fetchError } = await supabaseClient
					.from('leads_parcoursup')
					.select('status_before_hs, rappel_date_before_hs')
					.eq('id', id)
					.single()
				if ( fetchError ) throw new Error(fetchError.message)

				data.status = record.status_before_hs || null
				data.rappel_date = record.rappel_date_before_hs || null
			}

			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.update(data)
				.eq('id', id)
			if ( error ) throw new Error(error.message)
		} else {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.insert({ lead_id: leadId, ...data })
				.select()
			if ( error ) throw new Error(error.message)
		}

		return { data: { id: id || leadId } }
	},

	// Assign a lead to a pubeur (from pool)
	selfAssign: async ({ leadId, pubeurId }) => {
		const { error } = await supabaseClient
			.from('leads')
			.update({ assigned_pubeur_parcoursup: pubeurId })
			.eq('id', leadId)

		if ( error ) throw new Error(error.message)
		return { data: { id: leadId } }
	},

	// Schedule a callback: set rappel_date + status to arappeler
	scheduleCallback: async ({ id, leadId, rappelDate, statusHistory = [] }) => {
		const now = new Date().toISOString()
		const historyEntry = { from: null, to: 'arappeler', date: now }
		const data = {
			rappel_date: rappelDate,
			status: 'arappeler',
			status_history: [...statusHistory, historyEntry],
		}

		if ( id ) {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.update(data)
				.eq('id', id)
			if ( error ) throw new Error(error.message)
		} else {
			const { error } = await supabaseClient
				.from('leads_parcoursup')
				.insert({ lead_id: leadId, ...data })
				.select()
			if ( error ) throw new Error(error.message)
		}

		return { data: { id: id || leadId } }
	},
}

export const dataProvider = parcoursupDataProvider
